import { QuickFilter } from '@edsn/pie-components';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';
import { Card } from '#pie/components/card/Card';
import { DataTable } from '#pie/components/data-table/DataTable';
import { InputSearch } from '#pie/components/input-search/InputSearch';
import { Page, PageHeader } from '#pie/components/page/Page';
import { PageHead } from '#pie/components/page-head/PageHead';
import { Stack } from '#pie/components/stack/Stack';
import { Text } from '#pie/components/text/Text';
import { useDataTable } from '#pie/hooks/useDataTable';
import {
  getAllActiveOrganisations,
  type MarketParticipantStatus,
  type MarketParticipantType,
} from '#pie/internal/api/pie-bff';
import type { AvailableOrganisationFilters } from '#pie/internal/api/pie-bff/generated/model';
import {
  allowedMarketRoles,
  marketPartiesRegistrySortingAtom,
  marketPartyRegistryFiltersAtom,
} from '#pie/stores/marketPartiesRegistry';
import { searchAtom } from '#pie/stores/search';

const allowedMarketRolesSet: ReadonlySet<MarketParticipantType> = new Set(
  allowedMarketRoles,
);

const retainAvailable = <T,>(selected: T[] = [], available: T[] = []) => {
  const availableSet = new Set(available);
  return selected.filter((value) => availableSet.has(value));
};

export const MarketPartiesRegistry = () => {
  const { t } = useTranslation('marketParty');
  const { t: tcom } = useTranslation('common');
  const [search, setSearch] = useAtom(searchAtom);
  const [filters, setFilters] = useAtom(marketPartyRegistryFiltersAtom);

  const tableProps = useDataTable({
    columns: (helper) => [
      helper.accessor('statutoryName', {
        header: t('company.statutory_name'),
        meta: { columnWidth: 300 },
      }),
      helper.accessor((row) => `${row.statutoryName} ${row.marketRole}`, {
        id: 'marketParty',
        header: t('marketParty'),
        meta: { columnWidth: 300 },
        cell: (info) => {
          const row = info.row.original;
          const translatedRole = t(
            `marketParty.marketRole.${row.marketRole}_abbreviations`,
            { defaultValue: row.marketRole ?? '-' },
          );
          return `${row.statutoryName} (${translatedRole})`;
        },
      }),
      helper.accessor('ean13', {
        header: t('market_parties_registry.ean13'),
        meta: { columnWidth: 110 },
      }),
      helper.accessor('subunitNames', {
        header: t('marketParty.subregistration'),
        meta: { columnWidth: 300, className: 'hidden lg:table-cell' },
      }),
      helper.accessor('marketRole', {
        cell: (info) => {
          const value = info.getValue();
          return t(`marketParty.marketRole.${value}_abbreviations`, {
            defaultValue: value ?? '-',
          });
        },
        header: t('market_role'),
      }),
      helper.accessor('validStartDate', {
        header: t('startdate'),
      }),
      helper.accessor('status', {
        cell: (info) => {
          const value = info.getValue();
          return t(`status.${value}`, {
            defaultValue: value ?? '-',
          });
        },
        header: t('status'),
      }),
    ],
    dateKeys: [],
    filtersAtom: marketPartyRegistryFiltersAtom,
    query: getAllActiveOrganisations,
    sortingAtom: marketPartiesRegistrySortingAtom,
  });

  const rawFilters = tableProps.availableFilters as
    | AvailableOrganisationFilters
    | undefined;

  const availableMarketRoles =
    rawFilters?.marketRoles?.filter((marketRole) =>
      allowedMarketRolesSet.has(marketRole),
    ) ?? [];

  const availableStatuses = rawFilters?.statuses ?? [];

  const updateFilter = <Key extends 'marketRoles' | 'statuses'>(
    filter: Key,
    value: NonNullable<AvailableOrganisationFilters[Key]>,
  ) => {
    if (filter === 'marketRoles') {
      const retainedStatuses = retainAvailable(
        filters.statuses ?? [],
        availableStatuses,
      );
      setFilters({
        ...filters,
        marketRoles: value as MarketParticipantType[],
        statuses: retainedStatuses,
      });
    } else {
      const retainedMarketRoles = retainAvailable(
        filters.marketRoles ?? [],
        availableMarketRoles,
      );
      setFilters({
        ...filters,
        statuses: value as MarketParticipantStatus[],
        marketRoles: retainedMarketRoles,
      });
    }
  };

  const marketRoleOptions = availableMarketRoles.map((role) => ({
    label: t(`marketParty.marketRole.${role}_abbreviations`),
    value: role,
  }));

  const statusOptions = availableStatuses.map((status) => ({
    label: t(`marketParty.status.${status}`),
    value: status,
  }));

  return (
    <>
      <PageHead title={t('market_parties_registry.title')} />
      <Page>
        <PageHeader title={t('market_parties_registry.title')} />
        <Card className="flex-1 overflow-x-hidden p-4">
          <Stack gap="md">
            <div
              className="px-3 flex flex-row items-center gap-4"
              role="toolbar"
            >
              <InputSearch
                value={search || ''}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={tcom('common.search.placeholder')}
                onClear={() => setSearch('')}
              />
              <Text className="shrink-0">{tcom('common.filters')}</Text>
              <QuickFilter
                label={tcom('common.market_role')}
                options={marketRoleOptions}
                value={filters.marketRoles ?? []}
                onChange={(value) =>
                  updateFilter(
                    'marketRoles',
                    value as NonNullable<
                      AvailableOrganisationFilters['marketRoles']
                    >,
                  )
                }
              />
              <QuickFilter
                label={tcom('common.status')}
                options={statusOptions}
                value={filters.statuses ?? []}
                onChange={(value) =>
                  updateFilter(
                    'statuses',
                    value as NonNullable<
                      AvailableOrganisationFilters['statuses']
                    >,
                  )
                }
              />
            </div>
            <DataTable
              rowLinks={(row) => ({
                'aria-label': row.original.statutoryName,
                to: row.original.id!,
              })}
              {...tableProps}
              getRowId={(r) => r.id!}
            />
          </Stack>
        </Card>
      </Page>
    </>
  );
};
